import Action from "./action";

/**
 * Action to add an item to the cart.
 * 
 * For an example config object, see TODO!
 */
class CartAddV2Action extends Action {
  type = "cart.addV2";
  meta = {};

  /**
   * Constructor
   * @param {Object} config 
   */
  constructor(config = {}, mode = "live") {
    super(config, mode);

    try {
      this.hydrateMeta();
    } catch (e) {
      console.error(this.logPrefix, 'Failed to hydrate meta', e);
    }
  }

  /**
   * Override setter to add a property to the variant to ensure that the we can
   * identify this specific action to undo it if necessary.
   */
  set guid(value) {
    super.guid = value;

    // We need an Action-specific GUID so that, if multiple propositions attempt
    // to add the same variant to the cart, they don't get stuck in an infinite
    // loop of adding / removing the same item.
    // this.config.variants.forEach(variant => {
    //   variant.properties = { 
    //     ...(variant.properties || {}), "_r_add": value
    //   };
    // });
    this.config.properties = { 
      ...(this.config.properties || {}), "_r_add": value
    };
  }

  /**
   * Execute the action
   * @param {Object} data 
   */
  async execute(data) {
    this.log = [];
    console.groupCollapsed(this.logPrefix);

    if(this.mode !== "live"){
      console.log(`🚨 Simulation only in ${this.mode} mode`);
    }
    console.log('Config', this.config);
    console.log('Data', data);
    console.log('Meta', this.meta);

    // Add the first available variant from this.config.variants
    let variantToAdd = this.config.variants.find(variant => this.meta[variant.id].quantityAvailable > 0 || this.meta[variant.id].inventoryManagement === null);
    if(!variantToAdd){
      this.addLog(false, "No variants available", "⚠️");
      console.groupEnd();
      return;
    }

    let passesGates = true;
    Object.keys(this.config.gates || {}).forEach(key => {
      switch (key) {
        case "skipIfPreviouslyRemoved": 
          const removedItems = JSON.parse(localStorage.getItem('cart:removed-items') || '[]');

          const wasRemoved = removedItems.some(item =>
            item.variant_id === variantToAdd.id
          );
    
          if (wasRemoved) {
            console.log('Skipping as previously removed', variantToAdd);
            this.addLog(null, "Skipping as previously removed", "⚠️");
            passesGates = false;
          }
          break;
        default:
          break;
      }
    });

    if(!passesGates){
      console.groupEnd();
      return;
    }

    // Check if our serialized variant is already in the cart by serializing each item in the cart and comparing
    let serializedVariant = JSON.stringify({ id: variantToAdd.id, properties: this.config.properties });
    let existingCount = data.cart.filter(item => {
      let serializedExistingLineItem = JSON.stringify({ id: item.id, properties: item.properties });
      return serializedExistingLineItem === serializedVariant
    });

    if (existingCount.length < (this.config.limit || 1)) {
      // @ts-ignore
      let result;
      if(this.mode == "live"){
        result = await MP.Extensions.Cart.add({ id: variantToAdd.id, properties: this.config.properties, quantity: this.config.quantity });
      };
      console.log('Added!', result);
      this.addLog(result, "Added to cart");
    } else {
      console.log('Not adding as reached limit');
      this.addLog(null, "Reached limit", "⚠️");
    }
    console.groupEnd();
  }

  /**
   * Undo the action
   * @param {Object} data 
   */
  async undo(data) {
    this.log = [];
    console.group(this.logPrefix);
    let possibleIds = this.config.variants.map(variant => variant.id);
    let previouslyAddedLineItem = data.cart.find(item => {
      return possibleIds.includes(item.id) && JSON.stringify(item.properties) == JSON.stringify(this.config.properties || {});
    });

    if (previouslyAddedLineItem) {
      console.log('Undoing', this.config);
      // @ts-ignore
      let result;
      if(this.mode == "live"){
        result = await MP.Extensions.Cart.change({ id: previouslyAddedLineItem.key, quantity: 0 });
      }
      this.addLog(result, "Removed previously added item", "", "undo");
    } else {
      console.log('Nothing to undo', this.config);
      this.addLog(null, "No matching item", "⚠️", "undo");
    }
    console.groupEnd();
  }

  explainAction() { 
    let message = `Add ${this.config.strategy} of ${this.config.variants.map(
      variant => `${MP.Logger.variant({ title: this.meta[variant.id]?.title, id: variant.id })} from ${MP.Logger.product({ title: this.meta[variant.id]?.product.title, id: this.meta[variant.id]?.product.id.split("/").pop() })}`)
      .join(", ")} to cart with properties ${MP.Logger.properties(this.config.properties)}`;
    if(this.config.gates?.skipIfPreviouslyRemoved){
      message += ` if not previously removed by user`;
    }
    return message;
  }

  explainUndo() {
    if(this.config.bidirectional){
      return `Remove added variant from cart where properties match ${MP.Logger.properties(this.config.properties)}`;
    }
  }

  async hydrateMeta() {
    const graphQlIds = [
      ...(this.config.variants || []).map(variant => MP.Helpers.idToGraphQlId(variant.id, "variant"))   
    ];
    
    const result = await MP.Helpers.executeGQL(`{
      nodes(ids: ${JSON.stringify(graphQlIds)}) {
        ... on ProductVariant {
          id
          title
          quantityAvailable
          product {
            id
            title
          }
        }
      }
    }`, {});
    
    result.data.nodes.forEach(node => {
      this.meta[node.id.split("/").pop()] = node;
    });

    this.config.variants.forEach(async variant => {
      let response = await fetch(`/variants/${variant.id}.json`);
      let variantJson = await response.json();
      this.meta[variant.id].inventoryManagement = variantJson.product_variant.inventory_management;
    });
  }
}

export default CartAddV2Action;